import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Box,
  Container,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import EventIcon from '@mui/icons-material/Event'
import PersonIcon from '@mui/icons-material/Person'
import WorkIcon from '@mui/icons-material/Work'

import { verifyBooking } from '../services/api'

const DetailItem = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
    {icon}
    <Box>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='body1'>{value}</Typography>
    </Box>
  </Box>
)

function ConfirmBooking() {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    const verify = async () => {
      try {
        const data = await verifyBooking(token)
        setResult(data)
      } catch (err) {
        const responseData = err.response?.data
        if (responseData?.status === 'expired') {
          setError({
            title: 'Link expired',
            message:
              responseData.message ||
              'This confirmation link has expired. Please contact the business to request a new one.',
          })
        } else if (responseData?.status === 'already_used') {
          setError({
            title: 'Already confirmed',
            message:
              responseData.message ||
              'This booking has already been confirmed.',
          })
        } else {
          setError({
            title: 'Invalid link',
            message:
              responseData?.message ||
              'This confirmation link is invalid. Please check the link and try again.',
          })
        }
      } finally {
        setLoading(false)
      }
    }
    verify()
  }, [token])

  if (loading) {
    return (
      <Container maxWidth='sm' sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={60} />
          <Typography color='text.secondary'>Confirming your booking...</Typography>
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth='sm' sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant='h5' gutterBottom>
            {error.title}
          </Typography>
          <Typography color='text.secondary'>{error.message}</Typography>
        </Paper>
      </Container>
    )
  }

  const { booking, job } = result || {}
  const iconSx = { color: 'primary.main', fontSize: 22 }

  return (
    <Container maxWidth='sm' sx={{ py: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CheckCircleOutlineIcon
          sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
        />
        <Typography variant='h5' gutterBottom>
          Booking confirmed!
        </Typography>
        <Typography color='text.secondary' sx={{ mb: 3 }}>
          Your booking has been confirmed. You will receive a confirmation
          email with all the details shortly.
        </Typography>

        {(booking || job) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ textAlign: 'left', px: 2 }}>
              {booking?.date && (
                <DetailItem
                  icon={<EventIcon sx={iconSx} />}
                  label='Date'
                  value={new Date(booking.date).toLocaleDateString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                />
              )}
              {booking?.timeSlot && (
                <DetailItem
                  icon={<AccessTimeIcon sx={iconSx} />}
                  label='Time'
                  value={booking.timeSlot}
                />
              )}
              {booking?.engineerName && (
                <DetailItem
                  icon={<PersonIcon sx={iconSx} />}
                  label='Engineer'
                  value={booking.engineerName}
                />
              )}
              {job?.jobType && (
                <DetailItem
                  icon={<WorkIcon sx={iconSx} />}
                  label='Service'
                  value={job.jobType}
                />
              )}
            </Box>
          </>
        )}
      </Paper>
    </Container>
  )
}

export default ConfirmBooking
